/**
 * Tiny synthesized sound effects (Web Audio, no assets). Everything is optional: with no
 * AudioContext (tests, old browsers) every call is a no-op. `unlock()` must run inside a user
 * gesture (the start key) so the context is allowed to play.
 */
export type SfxName =
  | 'jump'
  | 'airJump'
  | 'land'
  | 'bone'
  | 'golden'
  | 'powerup'
  | 'spring'
  | 'hit'
  | 'shield'
  | 'levelUp'
  | 'gameOver'
  | 'start'
  | 'stomp';

type Ctx = AudioContext;

export class Sfx {
  private ctx: Ctx | null = null;
  private master: GainNode | null = null;
  muted = false;

  /** Creates/resumes the context; call from a user gesture. */
  unlock(): void {
    const Ctor = (globalThis as { AudioContext?: typeof AudioContext }).AudioContext;
    if (!Ctor) return;
    if (!this.ctx) {
      try {
        this.ctx = new Ctor();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.22;
        this.master.connect(this.ctx.destination);
      } catch {
        this.ctx = null;
        return;
      }
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : 0.22;
  }

  play(name: SfxName): void {
    if (!this.ctx || !this.master || this.muted) return;
    const t = this.ctx.currentTime;
    switch (name) {
      case 'jump':
        this.tone(t, 'square', 330, 660, 0.12, 0.25);
        break;
      case 'airJump':
        this.tone(t, 'square', 440, 880, 0.12, 0.25);
        break;
      case 'land':
        this.noise(t, 0.06, 0.18, 400);
        break;
      case 'bone':
        this.tone(t, 'sine', 880, 1320, 0.08, 0.3);
        break;
      case 'golden':
        this.tone(t, 'triangle', 660, 660, 0.08, 0.3);
        this.tone(t + 0.08, 'triangle', 880, 880, 0.08, 0.3);
        this.tone(t + 0.16, 'triangle', 1320, 1320, 0.14, 0.3);
        break;
      case 'powerup':
        this.tone(t, 'sawtooth', 300, 1200, 0.3, 0.2);
        break;
      case 'spring':
        this.tone(t, 'sine', 200, 900, 0.22, 0.3);
        break;
      case 'hit':
        this.noise(t, 0.25, 0.4, 900);
        this.tone(t, 'sawtooth', 220, 60, 0.3, 0.3);
        break;
      case 'shield':
        this.tone(t, 'sine', 700, 350, 0.25, 0.25);
        break;
      case 'levelUp':
        [523, 659, 784, 1047].forEach((f, i) =>
          this.tone(t + i * 0.1, 'triangle', f, f, 0.16, 0.3),
        );
        break;
      case 'gameOver':
        [440, 370, 311, 220].forEach((f, i) =>
          this.tone(t + i * 0.18, 'sawtooth', f, f * 0.98, 0.22, 0.25),
        );
        break;
      case 'start':
        [392, 523].forEach((f, i) => this.tone(t + i * 0.09, 'triangle', f, f, 0.14, 0.3));
        break;
      case 'stomp':
        this.noise(t, 0.08, 0.3, 700);
        this.tone(t, 'square', 260, 520, 0.12, 0.3);
        break;
    }
  }

  private tone(
    at: number,
    type: OscillatorType,
    from: number,
    to: number,
    seconds: number,
    volume: number,
  ): void {
    const ctx = this.ctx!;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(from, at);
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, to), at + seconds);
    gain.gain.setValueAtTime(volume, at);
    gain.gain.exponentialRampToValueAtTime(0.001, at + seconds);
    osc.connect(gain).connect(this.master!);
    osc.start(at);
    osc.stop(at + seconds + 0.02);
  }

  private noise(at: number, seconds: number, volume: number, cutoffHz: number): void {
    const ctx = this.ctx!;
    const length = Math.max(1, Math.floor(ctx.sampleRate * seconds));
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / length);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = cutoffHz;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(volume, at);
    gain.gain.exponentialRampToValueAtTime(0.001, at + seconds);
    src.connect(filter).connect(gain).connect(this.master!);
    src.start(at);
  }
}
